import { useMemo } from "react";
import { Play } from "lucide-react";
import { useSliderState } from "../SliderState";
import Analytics from "../../../../../utils/Analytics";
import localization from "../../../../../assets/localization";

interface VideosTemplateProps {
  template: any;
  language: string;
}

const parseVideos = (template: any): string[] => {
  const entries =
    template?.type === "video" ? [template] : template?.templates ?? [];
  return entries
    .map((entry: any) => entry?.video_url)
    .filter(Boolean) as string[];
};

// Native player over the same dark stage as the showcase. Thumbnails load
// only the first frame (`#t=0.001` keeps Safari from showing a black tile).
export default function VideosTemplate({
  template,
  language,
}: VideosTemplateProps) {
  const sliderState = useSliderState();
  const t = localization.get(language);
  const videos = useMemo(() => parseVideos(template), [template]);

  if (!videos.length) return null;

  const selectedIndex = Math.max(
    0,
    Math.min(sliderState.subTemplateItemIndex ?? 0, videos.length - 1)
  );
  const selected = videos[selectedIndex];

  return (
    <div className="videos-root">
      <div className="videos-stage">
        <video
          key={selected}
          className="videos-player"
          src={selected}
          controls
          playsInline
          preload="metadata"
          onPlay={() => Analytics.track("video_play", { url: selected })}
        />
        {videos.length > 1 && (
          <div className="showcase-context">
            {t.explore_videos}
            <span className="showcase-context-count">
              {selectedIndex + 1}/{videos.length}
            </span>
          </div>
        )}
      </div>

      {videos.length > 1 && (
        <div className="videos-rail-section">
          <div className="videos-rail">
            {videos.map((url, index) => (
              <button
                key={index}
                className={`videos-thumb ${
                  index === selectedIndex ? "active" : ""
                }`}
                onClick={() => sliderState.setSubTemplateItemIndex(index)}
                aria-label={`Video ${index + 1}`}
              >
                <video
                  src={`${url}#t=0.001`}
                  muted
                  playsInline
                  preload="metadata"
                />
                <span className="videos-thumb-play">
                  <Play className="icon" />
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
